import { DOCUMENT, Inject, Injectable, NgModule, NgZone } from '@angular/core';
import {
  EVENT_MANAGER_PLUGINS,
  EventManagerPlugin,
} from '@angular/platform-browser';
import {
  gestureEvents,
  GestureInput,
  PointerGestureRecognizer,
} from './pointer-gestures';

type GestureHandler = (event: GestureInput) => void;

/** One shared pointer session per element, with listeners kept outside Angular. */
class ElementGestures {
  private readonly handlers = new Map<string, Set<GestureHandler>>();
  private readonly recognizer = new PointerGestureRecognizer(
    (family) =>
      [...this.handlers.keys()].some((event) => event.startsWith(family)),
    (event) => {
      for (const handler of [...(this.handlers.get(event.type) ?? [])])
        handler(event);
    },
  );
  private readonly originalTouchAction: string;
  private readonly originalTouchActionPriority: string;
  private listening = false;
  private destroyed = false;

  constructor(
    private readonly element: HTMLElement,
    private readonly view: Window,
  ) {
    this.originalTouchAction = element.style.getPropertyValue('touch-action');
    this.originalTouchActionPriority =
      element.style.getPropertyPriority('touch-action');
    element.addEventListener('pointerdown', this.down);
    element.addEventListener('lostpointercapture', this.lostCapture);
    element.addEventListener('dragstart', this.preventNativeDrag);
  }

  add(event: string, handler: GestureHandler): () => boolean {
    const handlers = this.handlers.get(event) ?? new Set<GestureHandler>();
    handlers.add(handler);
    this.handlers.set(event, handlers);
    this.updateTouchAction();
    return () => {
      handlers.delete(handler);
      if (handlers.size === 0) this.handlers.delete(event);
      this.updateTouchAction();
      return this.handlers.size === 0;
    };
  }

  destroy(): void {
    this.destroyed = true;
    this.recognizer.destroy();
    this.stopListening();
    this.handlers.clear();
    this.element.removeEventListener('pointerdown', this.down);
    this.element.removeEventListener('lostpointercapture', this.lostCapture);
    this.element.removeEventListener('dragstart', this.preventNativeDrag);
    if (this.originalTouchAction) {
      this.element.style.setProperty(
        'touch-action',
        this.originalTouchAction,
        this.originalTouchActionPriority,
      );
    } else {
      this.element.style.removeProperty('touch-action');
    }
  }

  private updateTouchAction(): void {
    const names = [...this.handlers.keys()];
    const pinch = names.some((name) => name.startsWith('pinch'));
    const horizontal = names.some(
      (name) => name.startsWith('pan') || name.startsWith('swipe'),
    );
    // This is input routing, not presentation: configure it before pointerdown and restore on teardown.
    // Tap-only targets retain native scrolling/zoom; horizontal gestures retain vertical scrolling.
    this.element.style.setProperty(
      'touch-action',
      pinch ? 'none' : horizontal ? 'pan-y pinch-zoom' : 'manipulation',
    );
  }

  private readonly down = (event: PointerEvent): void => {
    this.recognizer.down(event);
    if (!this.recognizer.active || this.listening || this.destroyed) return;
    this.listening = true;
    // Window listeners avoid stealing capture from nested gesture targets and still follow drags outside.
    this.view.addEventListener('pointermove', this.move);
    this.view.addEventListener('pointerup', this.up);
    this.view.addEventListener('pointercancel', this.cancel);
    this.view.addEventListener('blur', this.blur);
  };

  private readonly move = (event: PointerEvent): void =>
    this.recognizer.move(event);
  private readonly up = (event: PointerEvent): void => {
    this.recognizer.up(event);
    if (!this.recognizer.active) this.stopListening();
  };
  private readonly cancel = (event: PointerEvent): void => {
    if (!this.recognizer.hasPointer(event.pointerId)) return;
    this.recognizer.cancel(event);
    this.stopListening();
  };
  private readonly lostCapture = (event: PointerEvent): void => {
    // A nested control releasing its own capture must not cancel the parent's gesture.
    if (event.target === this.element) this.cancel(event);
  };
  private readonly blur = (): void => {
    this.recognizer.cancel();
    this.stopListening();
  };
  private readonly preventNativeDrag = (event: DragEvent): void => {
    if (this.recognizer.active) event.preventDefault();
  };
  private stopListening(): void {
    if (!this.listening) return;
    this.listening = false;
    this.view.removeEventListener('pointermove', this.move);
    this.view.removeEventListener('pointerup', this.up);
    this.view.removeEventListener('pointercancel', this.cancel);
    this.view.removeEventListener('blur', this.blur);
  }
}

@Injectable()
export class GestureEventPlugin extends EventManagerPlugin {
  private readonly elements = new WeakMap<HTMLElement, ElementGestures>();
  constructor(
    @Inject(DOCUMENT) document: Document,
    private readonly zone: NgZone,
  ) {
    super(document);
  }
  override supports(eventName: string): boolean {
    return gestureEvents.has(eventName.toLowerCase());
  }
  override addEventListener(
    element: HTMLElement,
    eventName: string,
    handler: GestureHandler,
  ): () => void {
    return this.zone.runOutsideAngular(() => {
      const view = element.ownerDocument.defaultView;
      if (!view) return () => {};
      let entry = this.elements.get(element);
      if (!entry) {
        entry = new ElementGestures(element, view);
        this.elements.set(element, entry);
      }
      const registration = entry;
      const remove = registration.add(eventName.toLowerCase(), (event) =>
        this.zone.runGuarded(() => handler(event)),
      );
      let disposed = false;
      return () => {
        if (disposed) return;
        disposed = true;
        if (remove()) {
          registration.destroy();
          this.elements.delete(element);
        }
      };
    });
  }
}

@NgModule({
  providers: [
    {
      provide: EVENT_MANAGER_PLUGINS,
      useClass: GestureEventPlugin,
      multi: true,
    },
  ],
})
export class GestureModule {}
